import { DataProvider, HttpError } from 'react-admin';

import { requireAuth, requirePartner } from '@src/providers/dataProvider/shared';

import { fetchVitalElements } from './fetchVitalElements';
import type { VitalElementsRecord } from './types';

interface VitalElementsDataProvider {
  getList: () => Promise<{ data: VitalElementsRecord[], total: number }>;
  getOne: (resource: string, params: { id: string }) => Promise<{ data: VitalElementsRecord }>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: () => Promise<never>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

/**
 * Data provider for vitalElements resource.
 *
 * Vital elements are data-only (no UI resource) and provide the list of
 * facet tags a partner has chosen as important product properties.
 */
const vitalElementsDataProviderImpl: VitalElementsDataProvider = {
  getList: async () => {
    try {
      const token = requireAuth();
      const partner = requirePartner();
      const vitalElements = await fetchVitalElements({ partnerCode: partner.code, token });

      const record: VitalElementsRecord = {
        id: partner.code,
        vitalElements,
        partnerCode: partner.code,
      };

      return {
        data: [record],
        total: 1,
      };
    } catch (error) {
      console.error('vitalElementsDataProvider.getList failed:', {
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load vital elements.', status);
    }
  },

  getOne: async (resource, params) => {
    try {
      const token = requireAuth();
      const partner = requirePartner();
      const vitalElements = await fetchVitalElements({ partnerCode: partner.code, token });

      return {
        data: {
          id: params.id,
          vitalElements,
          partnerCode: partner.code,
        },
      };
    } catch (error) {
      console.error('vitalElementsDataProvider.getOne failed:', {
        params,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load vital elements.', status);
    }
  },

  getMany: async () => {
    throw new Error('getMany not implemented for vitalElements resource');
  },

  getManyReference: async () => {
    throw new Error('getManyReference not implemented for vitalElements resource');
  },

  create: async () => {
    throw new Error('create not implemented for vitalElements resource');
  },

  update: async () => {
    throw new Error('update not implemented for vitalElements resource');
  },

  updateMany: async () => {
    throw new Error('updateMany not implemented for vitalElements resource');
  },

  delete: async () => {
    throw new Error('delete not implemented for vitalElements resource');
  },

  deleteMany: async () => {
    throw new Error('deleteMany not implemented for vitalElements resource');
  },
};

// Cast to DataProvider for react-admin compatibility.
export const vitalElementsDataProvider = vitalElementsDataProviderImpl as unknown as DataProvider;

export default vitalElementsDataProvider;
